export interface PasswordRequirement {
  id: string;
  text: string;
  met: boolean;
}

export type StrengthLevel = 'weak' | 'fair' | 'strong' | 'very-strong';

export interface PasswordStrengthResult {
  score: number; // 0-4
  level: StrengthLevel;
  requirements: PasswordRequirement[];
  allRequirementsMet: boolean;
}

export interface BreachCheckResult {
  breached: boolean;
  count: number;
}

export function getPasswordRequirements(password: string): PasswordRequirement[] {
  return [
    { id: 'length', text: 'At least 8 characters', met: password.length >= 8 },
    { id: 'uppercase', text: 'One uppercase letter (A-Z)', met: /[A-Z]/.test(password) },
    { id: 'lowercase', text: 'One lowercase letter (a-z)', met: /[a-z]/.test(password) },
    { id: 'number', text: 'One number (0-9)', met: /\d/.test(password) },
  ];
}

export function calculatePasswordStrength(password: string): PasswordStrengthResult {
  const requirements = getPasswordRequirements(password);
  const allRequirementsMet = requirements.every((r) => r.met);

  let score = 0;

  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score++;
  if (/\d/.test(password) && /[^a-zA-Z\d]/.test(password)) score++;

  const levelMap: Record<number, StrengthLevel> = {
    0: 'weak',
    1: 'weak',
    2: 'fair',
    3: 'strong',
    4: 'very-strong',
  };

  return {
    score,
    level: levelMap[score] ?? 'weak',
    requirements,
    allRequirementsMet,
  };
}

/**
 * Check password against HaveIBeenPwned using k-anonymity.
 * Only the first 5 characters of the SHA-1 hash are sent to the API.
 */
export async function checkPasswordBreach(password: string): Promise<BreachCheckResult> {
  try {
    const encoder = new TextEncoder();
    const data = encoder.encode(password);
    const hashBuffer = await crypto.subtle.digest('SHA-1', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('').toUpperCase();

    const prefix = hashHex.slice(0, 5);
    const suffix = hashHex.slice(5);

    const response = await fetch(`https://api.pwnedpasswords.com/range/${prefix}`, {
      headers: { 'Add-Padding': 'true' },
    });

    if (!response.ok) {
      return { breached: false, count: 0 };
    }

    const text = await response.text();
    const lines = text.split('\n');

    for (const line of lines) {
      const [hashSuffix, countStr] = line.split(':');
      if (hashSuffix?.trim() === suffix) {
        const count = parseInt(countStr?.trim() ?? '0', 10);
        if (count > 0) {
          return { breached: true, count };
        }
      }
    }

    return { breached: false, count: 0 };
  } catch {
    return { breached: false, count: 0 };
  }
}
